import { useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { WEEKDAYS_IT, monthLabel, formatDateIT } from "@/lib/dates";
import { StatusBadge } from "@/components/StatusBadge";

// holidays: [{start_date, end_date, status, employee_name}]
export const MonthCalendar = ({ date, onPrev, onNext, holidays = [] }) => {
  const [selectedDay, setSelectedDay] = useState(null);
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const numDays = new Date(year, month + 1, 0).getDate();
  const offset = (firstDay.getDay() + 6) % 7; // Monday-first

  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= numDays; d++) cells.push(d);

  const iso = (d) =>
    `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  const dayStatuses = (d) => {
    const day = iso(d);
    const set = new Set();
    holidays.forEach((h) => {
      if (h.start_date <= day && day <= h.end_date && h.status !== "rejected") {
        set.add(h.status);
      }
    });
    return set;
  };

  const todayIso = (() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
  })();

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="month-calendar">
      <div className="flex items-center justify-between mb-4">
        <button
          data-testid="calendar-prev"
          onClick={onPrev}
          className="h-11 w-11 rounded-xl border border-slate-200 flex items-center justify-center active:scale-95 text-slate-600"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <span className="font-head font-bold text-lg text-slate-900" data-testid="calendar-label">
          {monthLabel(date)}
        </span>
        <button
          data-testid="calendar-next"
          onClick={onNext}
          className="h-11 w-11 rounded-xl border border-slate-200 flex items-center justify-center active:scale-95 text-slate-600"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center mb-1">
        {WEEKDAYS_IT.map((w) => (
          <div key={w} className="text-xs font-semibold text-slate-400 py-1">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null) return <div key={i} />;
          const statuses = dayStatuses(d);
          const isToday = iso(d) === todayIso;
          const isSelected = iso(d) === selectedDay;
          const hasHol = statuses.size > 0;
          return (
            <button
              key={i}
              type="button"
              data-testid={`cal-day-${d}`}
              onClick={() => hasHol && setSelectedDay(iso(d))}
              className={`aspect-square rounded-lg flex flex-col items-center justify-center text-sm relative ${
                isSelected ? "ring-2 ring-primary" : ""
              } ${isToday ? "bg-accent font-bold text-primary" : "text-slate-700"} ${
                hasHol ? "cursor-pointer" : "cursor-default"
              }`}
            >
              <span>{d}</span>
              {hasHol && (
                <div className="flex gap-0.5 mt-0.5">
                  {statuses.has("accepted") && <span className="h-1.5 w-1.5 rounded-full bg-green-500" />}
                  {statuses.has("pending") && <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />}
                </div>
              )}
            </button>
          );
        })}
      </div>
      <div className="flex gap-4 mt-3 text-xs text-slate-500">
        <span className="flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-amber-500" /> In attesa
        </span>
        <span className="flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-green-500" /> Accettata
        </span>
      </div>

      {selectedDay && selectedDay.startsWith(`${year}-${String(month + 1).padStart(2, "0")}`) && (() => {
        const dayHols = holidays.filter(
          (h) => h.start_date <= selectedDay && selectedDay <= h.end_date && h.status !== "rejected"
        );
        return (
          <div data-testid="day-people-panel" className="mt-4 border-t border-slate-200 pt-3">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-slate-900">In ferie il {formatDateIT(selectedDay)}</span>
              <button data-testid="close-day-panel" onClick={() => setSelectedDay(null)} className="text-slate-400"><X className="h-4 w-4" /></button>
            </div>
            {dayHols.length === 0 ? (
              <p className="text-sm text-slate-400">Nessuno in ferie questo giorno.</p>
            ) : (
              <div className="space-y-2">
                {dayHols.map((h) => (
                  <div key={h.id} className="flex items-center justify-between">
                    <span className="font-medium text-slate-700">{h.employee_name}</span>
                    <StatusBadge status={h.status} />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};
